import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { EmojiProvider } from '@atlaskit/emoji/types';
import EmojiResource from '@atlaskit/emoji/emoji-resource';

import type { RemixFeedbackMetadata } from '@atlassian/conversation-assistant-action-helpers/utils/confluence-actions';

import { expVal } from '@confluence/feature-experiments';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs.ts';
import type { CustomGreetingConfig } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import type { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';

import { createNavigateToObject } from '../utils/navigation.ts';
import type { FeedbackVariant } from '../utils/FeedbackVariant.ts';
import { useLazyShowFeedbackFlag } from '../utils/useLazyShowFeedbackFlag.ts';

const CLOSE_MODAL_DELAY = 500;
const AGENT_MENU_CREATE_SOURCE = 'confluence-rovo-agent-menu-create-agent';

export const useConversationAssistantConfig = ({
	assistanceServiceConfigHeaders = {},
	preventAbortRequestsOnStagingModalClose = false,
	preventAbortRequestsOnStandaloneChatClose = false,
}: {
	assistanceServiceConfigHeaders?: Record<string, string>;
	preventAbortRequestsOnStagingModalClose?: boolean;
	preventAbortRequestsOnStandaloneChatClose?: boolean;
}): {
	displaySubmitFlag: boolean;
	siteId: string;
	product: string;
	orgId: string | undefined;
	assistanceServiceConfig: {
		headers: Record<string, string>;
		baseUrl: string;
	};
	emojiProvider: Promise<EmojiProvider>;
	userId: string | undefined;
	preventAbortRequestsOnStagingModalClose: boolean;
	preventAbortRequestsOnStandaloneChatClose: boolean;
} => {
	const { cloudId, orgId, userId } = useSessionData();
	const emojiProvider = useMemo(() => {
		return getEmojiProvider(EmojiResource, {
			cloudId,
			userId,
		});
	}, [userId, cloudId]);

	// Keep the config object identity stable when its semantic inputs are stable.
	const assistanceServiceConfig = useMemo(
		() => ({
			headers: assistanceServiceConfigHeaders,
			baseUrl: '/gateway/api/assist',
		}),
		[assistanceServiceConfigHeaders],
	);

	return useMemo(
		() => ({
			displaySubmitFlag: true,
			siteId: cloudId,
			product: 'confluence',
			orgId,
			assistanceServiceConfig,
			emojiProvider,
			userId: userId ?? undefined,
			preventAbortRequestsOnStagingModalClose,
			preventAbortRequestsOnStandaloneChatClose,
		}),
		[
			assistanceServiceConfig,
			cloudId,
			emojiProvider,
			orgId,
			preventAbortRequestsOnStagingModalClose,
			preventAbortRequestsOnStandaloneChatClose,
			userId,
		],
	);
};

export const useConversationAssistantUiConfig = ({
	flags,
	customGreeting,
	shouldShowGreeting,
	shouldHideFeedback,
}: {
	flags: FlagsStateContainer;
	customGreeting?: CustomGreetingConfig;
	shouldShowGreeting?: boolean;
	shouldHideFeedback?: boolean;
}): {
	customHandlers: {
		onCreateAgentClick: () => void;
		handleSubmitSmartCreate: ({
			baseUrl,
			path,
		}: {
			baseUrl: string;
			path: string;
		}) => Promise<void>;
		handleCloseSmartCreate: ({
			feedbackVariant,
			sourceEntryPoint,
			currentConversationId,
			traceId,
			requestId,
			remixMetadata,
			contentType,
			contentMode,
		}: {
			feedbackVariant?: FeedbackVariant;
			sourceEntryPoint?: string;
			currentConversationId?: string | null;
			traceId?: string;
			requestId?: string;
			remixMetadata?: RemixFeedbackMetadata;
			contentType?: string;
			contentMode?: PageMode;
		}) => void;
	};
	elements: {
		voiceDictationButton: boolean;
		shouldShowGreeting: boolean | undefined;
	};
	customGreeting: CustomGreetingConfig | undefined;
} => {
	const { push } = useRouteActions();
	const intl = useIntl();
	const { showModal } = useDialogs();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const { openModal } = useSmartCreationModalActions();

	const navigateToObject = useCallback(
		async ({ baseUrl: baseUrlNav, path }: { baseUrl: string; path: string }) => {
			createNavigateToObject(push)({ baseUrl: baseUrlNav, path });
			// artifical delay before closing the modal gives the app a moment to transition to the created object while the modal is still open, this creates a smoother transition from modal to new object.
			// For non-SPA transitions, a full page reload will happen so this delay will be ignored
			await new Promise((r) => setTimeout(r, CLOSE_MODAL_DELAY));
		},
		[push],
	);

	const showCreateWithRovoModalCloseFeedbackFlag = useLazyShowFeedbackFlag(
		flags,
		intl,
		showModal,
		createAnalyticsEvent,
	);

	const handleCloseSmartCreate = useCallback(
		({
			feedbackVariant,
			sourceEntryPoint,
			currentConversationId,
			traceId,
			requestId,
			remixMetadata,
			contentType,
			contentMode,
		}: {
			feedbackVariant?: FeedbackVariant;
			sourceEntryPoint?: string;
			currentConversationId?: string | null;
			traceId?: string;
			requestId?: string;
			remixMetadata?: RemixFeedbackMetadata;
			contentType?: string;
			contentMode?: PageMode;
		}) => {
			if (shouldHideFeedback) {
				return;
			}

			if (feedbackVariant === undefined) {
				return;
			}

			void showCreateWithRovoModalCloseFeedbackFlag({
				feedbackVariant,
				sourceEntryPoint,
				contentType,
				contentMode,
				cloudId,
				currentConversationId: currentConversationId ?? undefined,
				traceId,
				requestId,
				remixMetadata,
			});
		},
		[showCreateWithRovoModalCloseFeedbackFlag, shouldHideFeedback, cloudId],
	);

	const handleCreateAgent = useCallback(() => {
		if (!expVal('cc_contextual_agent_creation_v2', 'isEnabled', false)) {
			return;
		}

		openModal({
			entryPoint: AGENT_MENU_CREATE_SOURCE,
			initialOutcomeType: 'agent',
			lockInitialOutcomeType: true,
		});
	}, [openModal]);

	// Keep uiConfig identity stable when handler/greeting inputs are stable.
	return useMemo(
		() => ({
			customHandlers: {
				onCreateAgentClick: handleCreateAgent,
				handleSubmitSmartCreate: navigateToObject,
				handleCloseSmartCreate,
			},
			elements: {
				voiceDictationButton: true,
				shouldShowGreeting: shouldShowGreeting ?? undefined,
			},
			customGreeting: customGreeting ?? undefined,
		}),
		[
			customGreeting,
			handleCloseSmartCreate,
			handleCreateAgent,
			navigateToObject,
			shouldShowGreeting,
		],
	);
};
